import { Toaster } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import NotFound from "@/pages/NotFound";
import { Route, Switch } from "wouter";
import ErrorBoundary from "./components/ErrorBoundary";
import { ProtectedRoute } from "./components/ProtectedRoute";
import { AdminRoute } from "./components/AdminRoute";
import { TeamAdminRoute } from "./components/TeamAdminRoute";
import { ThemeProvider } from "./contexts/ThemeContext";
import { LanguageProvider } from "./contexts/LanguageContext";
import { AuthProvider } from "./contexts/AuthContext";

// Marketing pages
import Home from "./pages/Home";
import ScienceLLMs from "./pages/ScienceLLMs";
import Docs from "./pages/Docs";
import Login from "./pages/Login";
import ChangePassword from "./pages/ChangePassword";

// Console pages
import Dashboard from "./pages/console/Dashboard";
import ApiKeys from "./pages/console/ApiKeys";
import ConsoleLLMs from "./pages/console/ConsoleLLMs";
import ScienceAgentMarketplace from "./pages/console/ScienceAgentMarketplace";
import ScienceAgentDetail from "./pages/console/ScienceAgentDetail";
import ExperimentLogs from "./pages/console/ExperimentLogs";
import Settings from "./pages/console/Settings";

// Team pages
import TeamUsage from "./pages/console/team/TeamUsage";
import TeamManagement from "./pages/console/team/TeamManagement";
import TeamWalletTransactions from "./pages/console/team/TeamWalletTransactions";

// Admin pages
import AdminTeams from "./pages/console/admin/AdminTeams";
import AdminProviders from "./pages/console/admin/AdminProviders";
import ProviderWizard from "./pages/console/admin/ProviderWizard";
import AdminProviderModels from "./pages/console/admin/AdminProviderModels";
import AdminScienceLLMs from "./pages/console/admin/AdminScienceLLMs";
import AdminUsage from "./pages/console/admin/AdminUsage";
import AdminUsageAttempts from "./pages/console/admin/AdminUsageAttempts";
import AdminBills from "./pages/console/admin/AdminBills";
import AdminWalletTransactions from "./pages/console/admin/AdminWalletTransactions";

function Router() {
  return (
    <Switch>
      {/* Marketing pages */}
      <Route path="/" component={Home} />
      <Route path="/llms" component={ScienceLLMs} />
      <Route path="/docs" component={Docs} />
      <Route path="/login" component={Login} />
      <Route path="/change-password" component={ChangePassword} />

      {/* Console pages (protected) */}
      <Route path="/console">
        <ProtectedRoute>
          <Dashboard />
        </ProtectedRoute>
      </Route>
      <Route path="/console/dashboard">
        <ProtectedRoute>
          <Dashboard />
        </ProtectedRoute>
      </Route>
      <Route path="/console/keys">
        <ProtectedRoute>
          <ApiKeys />
        </ProtectedRoute>
      </Route>
      <Route path="/console/llms">
        <ProtectedRoute>
          <ConsoleLLMs />
        </ProtectedRoute>
      </Route>
      <Route path="/console/science-agents/:agentKey">
        <ProtectedRoute>
          <ScienceAgentDetail />
        </ProtectedRoute>
      </Route>
      <Route path="/console/science-agents">
        <ProtectedRoute>
          <ScienceAgentMarketplace />
        </ProtectedRoute>
      </Route>
      <Route path="/console/logs">
        <ProtectedRoute>
          <ExperimentLogs />
        </ProtectedRoute>
      </Route>
      <Route path="/console/settings">
        <ProtectedRoute>
          <Settings />
        </ProtectedRoute>
      </Route>

      {/* Team admin pages (team_admin or system_admin) */}
      <Route path="/console/team">
        <TeamAdminRoute>
          <TeamManagement />
        </TeamAdminRoute>
      </Route>
      <Route path="/console/team/members">
        <TeamAdminRoute>
          <TeamManagement />
        </TeamAdminRoute>
      </Route>
      <Route path="/console/team/usage">
        <TeamAdminRoute>
          <TeamUsage />
        </TeamAdminRoute>
      </Route>
      <Route path="/console/team/wallet-transactions">
        <TeamAdminRoute>
          <TeamWalletTransactions />
        </TeamAdminRoute>
      </Route>

      {/* Admin pages (system_admin only) */}
      <Route path="/console/admin">
        <AdminRoute>
          <AdminTeams />
        </AdminRoute>
      </Route>
      <Route path="/console/admin/teams">
        <AdminRoute>
          <AdminTeams />
        </AdminRoute>
      </Route>
      <Route path="/console/admin/providers">
        <AdminRoute>
          <AdminProviders />
        </AdminRoute>
      </Route>
      <Route path="/console/admin/providers/new">
        <AdminRoute>
          <ProviderWizard />
        </AdminRoute>
      </Route>
      <Route path="/console/admin/providers/:id/models">
        <AdminRoute>
          <AdminProviderModels />
        </AdminRoute>
      </Route>
      <Route path="/console/admin/science-llms">
        <AdminRoute>
          <AdminScienceLLMs />
        </AdminRoute>
      </Route>
      <Route path="/console/admin/usage">
        <AdminRoute>
          <AdminUsage />
        </AdminRoute>
      </Route>
      <Route path="/console/admin/usage-attempts">
        <AdminRoute>
          <AdminUsageAttempts />
        </AdminRoute>
      </Route>
      <Route path="/console/admin/bills">
        <AdminRoute>
          <AdminBills />
        </AdminRoute>
      </Route>
      <Route path="/console/admin/wallet-transactions">
        <AdminRoute>
          <AdminWalletTransactions />
        </AdminRoute>
      </Route>

      <Route path="/404" component={NotFound} />
      <Route component={NotFound} />
    </Switch>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <ThemeProvider>
        <LanguageProvider>
          <AuthProvider>
            <TooltipProvider>
              <Toaster />
              <Router />
            </TooltipProvider>
          </AuthProvider>
        </LanguageProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
}

export default App;
